import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CalendarCheck, Clock, CreditCard } from 'lucide-react';
import { ModeNotificationsContext, FournisseurEnregistrement, EtatEnregistrement, GroupeNotifications } from '../ui/LigneNotification';
import { NotificationBlock, insertNotificationToken } from '../PrestationPlanning/PrestationNotificationsPage';
import { Input } from '../ui/Field';
import { messageErreur, reservationsMessagesApi, type MessageClient } from '../../lib/reservationsApi';
import { useCodeManager } from './useCodeManager';

/**
 * Messages aux clients du livre (comme les « Notifications clients » de ZenChef) : une ligne par message — titre, quand il part,
 * interrupteur —, et un clic ouvre sa fenêtre : objet, texte avec mise en forme et jetons ({{prenom}}, {{date}}…), boutons à placer,
 * aperçu de ce que reçoit le client et « Tester l'e-mail ». Chaque changement s'enregistre tout de suite.
 */
const ICONES: Record<string, React.ReactNode> = { Réservation: <CalendarCheck size={15} />, 'Avant la venue': <Clock size={15} />, Paiements: <CreditCard size={15} /> };

const BlocMessage: React.FC<{ m: MessageClient; jetons: { token: string; label: string }[]; maj: (patch: Partial<MessageClient>) => void }> = ({ m, jetons, maj }) => {
    const [champ, setChamp] = useState<'subject' | 'body'>('body');
    const sujetRef = useRef<HTMLInputElement>(null);
    const corpsRef = useRef<HTMLTextAreaElement>(null);
    const inserer = (token: string) => {
        if (champ === 'subject') insertNotificationToken(sujetRef.current, m.sujet, (v: string) => maj({ sujet: v }), token);
        else insertNotificationToken(corpsRef.current, m.corps, (v: string) => maj({ corps: v }), token);
    };
    return (
        <NotificationBlock title={m.titre} description={m.quand}
            toggle={{ checked: m.actif, onChange: (v) => maj({ actif: v }) }}
            subject={m.sujet} setSubject={(v) => maj({ sujet: v })} body={m.corps} setBody={(v) => maj({ corps: v })}
            subjectRef={sujetRef} bodyRef={corpsRef} activeField={champ} setActiveField={setChamp} onInsertToken={inserer} tokens={jetons}
            extra={(
                <>
                    {m.type === 'rappel' && (
                        <label className="flex flex-wrap items-center gap-2 text-[13px] text-content">
                            Envoyer
                            <Input aria-label="Heures avant la réservation" inputMode="numeric" className="!w-[70px] max-lg:!h-11" value={String(m.reglage?.heures_avant ?? 24)}
                                onChange={(e) => maj({ reglage: { ...m.reglage, heures_avant: Number(e.target.value.replace(/\D/g, '').slice(0, 3)) || 0 } })} />
                            heures avant la réservation
                        </label>
                    )}
                    {(m.sujet !== m.defaut.sujet || m.corps !== m.defaut.corps) && (
                        <button type="button" className="text-[12.5px] font-semibold underline min-h-[28px] max-lg:min-h-[44px]" onClick={() => maj({ sujet: m.defaut.sujet, corps: m.defaut.corps })}>Revenir au texte proposé</button>
                    )}
                </>
            )} />
    );
};

export const MessagesClientsReglages: React.FC = () => {
    const { executer, fenetre } = useCodeManager();
    const [messages, setMessages] = useState<MessageClient[] | null>(null);
    const [jetons, setJetons] = useState<{ token: string; label: string }[]>([]);
    const [etat, setEtat] = useState<'repos' | 'en_cours' | 'fait' | 'echec'>('repos');
    const [erreur, setErreur] = useState('');
    const courant = useRef<MessageClient[] | null>(null);
    courant.current = messages;

    useEffect(() => {
        reservationsMessagesApi.lire().then((r) => { setMessages(r.messages); setJetons(r.jetons); }).catch((e) => setErreur(messageErreur(e)));
    }, []);

    const enregistrer = useCallback(async () => {
        const liste = courant.current; if (!liste) return;
        setEtat('en_cours'); setErreur('');
        try {
            const r = await executer((code) => reservationsMessagesApi.enregistrer(Object.fromEntries(liste.map((m) => [m.type, { actif: m.actif, sujet: m.sujet, corps: m.corps, reglage: m.reglage }])), code));
            setEtat(r ? 'fait' : 'repos');
        } catch (e) { setEtat('echec'); setErreur(messageErreur(e)); }
    }, [executer]);

    const maj = (type: string, patch: Partial<MessageClient>) => setMessages((l) => (l ? l.map((m) => (m.type === type ? { ...m, ...patch } : m)) : l));
    const groupes = messages ? [...new Set(messages.map((m) => m.groupe))] : [];

    return (
        <section aria-labelledby="titre-messages" className="space-y-3">
            <div className="flex flex-wrap items-end justify-between gap-2">
                <div>
                    <h2 id="titre-messages" className="text-[15px] font-semibold text-content">Messages aux clients</h2>
                    <p className="text-[12.5px] text-content-muted mt-0.5">Les e-mails que reçoivent vos clients. Activez ceux que vous voulez et écrivez-les à votre façon : les jetons comme {'{{prenom}}'} ou {'{{date}}'} se remplacent tout seuls.</p>
                </div>
                <EtatEnregistrement etat={etat} />
            </div>
            {erreur && <p role="alert" className="rounded-xl bg-state-danger-soft text-state-danger px-3.5 py-2.5 text-[13px] font-semibold">{erreur}</p>}
            {!messages ? (!erreur && <p className="text-[13px] text-content-muted">Chargement…</p>) : (
                <ModeNotificationsContext.Provider value={{ enLignes: true }}>
                    <FournisseurEnregistrement enregistrer={enregistrer}>
                        <div className="space-y-4">
                            {groupes.map((g) => (
                                <GroupeNotifications key={g} titre={g} icone={ICONES[g]}>
                                    {messages.filter((m) => m.groupe === g).map((m) => <BlocMessage key={m.type} m={m} jetons={jetons} maj={(p) => maj(m.type, p)} />)}
                                </GroupeNotifications>
                            ))}
                        </div>
                    </FournisseurEnregistrement>
                </ModeNotificationsContext.Provider>
            )}
            {fenetre}
        </section>
    );
};
